import React, { useRef, useState } from 'react';
import { getLatestVersion, getVersionLabel, uploadNewVersion, cardOf } from './storage';
import {
  EstimateVersion, EstimateKind, ESTIMATE_KINDS, formatEstimateKind,
  RateCard, RATE_CARDS, formatRateCard, formatEstimateStream,
} from './types';
import { getEmailFromSessionStorage } from '../utils/session_utils';

interface Props {
  /** Bumped after a successful upload so other tabs can re-read. */
  onUploaded: (v: EstimateVersion) => void;
}

type State =
  | { kind: 'idle' }
  /** File picked/dropped — waiting for user to confirm before parsing. */
  | { kind: 'pending'; file: File }
  | { kind: 'parsing'; fileName: string }
  | { kind: 'errors'; errors: string[]; fileName: string }
  | { kind: 'saved'; version: EstimateVersion };

const UploadTab: React.FC<Props> = ({ onUploaded }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [dragOver, setDragOver] = useState(false);
  const [latest, setLatest] = useState<EstimateVersion | null>(() => getLatestVersion());
  // Type of estimate being uploaded — 'upfront' (default) or 'scatter'.
  // Threaded into uploadNewVersion / saveVersion, ends up as EST_VERSION.kind
  // in Snowflake. Version sequence is per (year, kind) so each track has
  // its own V1/V2/… count.
  const [uploadKind, setUploadKind] = useState<EstimateKind>('upfront');
  // Which agency rate card this file belongs to — 'general' (the master
  // estimate, and the default) or a custom card such as 'wpp'. Orthogonal to
  // kind: the version sequence is per (year, kind, rateCard), so WPP gets its
  // own V1 rather than continuing the general master's numbering.
  //
  // Only meaningful for upfront — there is no WPP scatter. The picker is
  // hidden when Scatter is selected and the value is clamped by the derived
  // `effectiveRateCard` below (the raw state is intentionally left alone, so
  // toggling Scatter → Upfront restores the previous pick). uploadNewVersion
  // clamps again, and the backend rejects the combination outright.
  const [uploadRateCard, setUploadRateCard] = useState<RateCard>('general');
  const effectiveRateCard: RateCard = uploadKind === 'upfront' ? uploadRateCard : 'general';

  // Selecting a file no longer auto-uploads — it stages the file so the
  // user can sanity-check the name before committing. Confirm runs parse +
  // save; Cancel discards.
  const stageFile = (file: File) => setState({ kind: 'pending', file });

  const confirmUpload = async () => {
    if (state.kind !== 'pending') return;
    const file = state.file;
    setState({ kind: 'parsing', fileName: file.name });
    const uploadedBy = getEmailFromSessionStorage() || undefined;
    const result = await uploadNewVersion(file, uploadedBy, uploadKind, effectiveRateCard);
    if (!result.ok) {
      setState({ kind: 'errors', errors: result.errors, fileName: file.name });
      return;
    }
    setState({ kind: 'saved', version: result.version });
    // Re-derive rather than assuming the thing we just uploaded is now "the
    // latest". getLatestVersion() is general-only by design, and this card
    // renders a bare version label with no stream chip — so assigning a WPP
    // upload here would show "V1" beside the general master's real V4 with
    // nothing to tell them apart. (Same was true of scatter uploads before
    // rate cards existed.)
    setLatest(getLatestVersion());
    onUploaded(result.version);
  };

  const reset = () => setState({ kind: 'idle' });

  const onPick = () => fileInputRef.current?.click();
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) stageFile(f);
    e.target.value = '';
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) stageFile(f);
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);

  return (
    <div style={{ padding: '24px 28px 28px' }}>
      {/* Latest version card */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 18px', marginBottom: 20,
        background: latest ? '#f8fafc' : '#fffbeb',
        border: `1px solid ${latest ? '#e2e8f0' : '#fde68a'}`,
        borderRadius: 10,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 999,
          background: latest ? '#dbeafe' : '#fbbf24',
          color: latest ? '#1e40af' : '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 800, flexShrink: 0,
        }}>
          {latest ? getVersionLabel(latest) : '!'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 1 }}>
            Current Latest Version
          </div>
          {latest ? (
            <div style={{ fontSize: 12.5, color: '#475569' }}>
              <strong style={{ color: '#0f172a', fontWeight: 600 }}>
                As of {formatDate(latest.asOfDate)}
              </strong>
              {' · '}{latest.rowCount.toLocaleString()} rows
              {' · '}
              <span style={{
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                display: 'inline-block', maxWidth: 360, verticalAlign: 'bottom',
              }}>{latest.fileName}</span>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: '#92400e' }}>
              No estimate version has been uploaded yet.
            </div>
          )}
        </div>
      </div>

      {/* Drop zone — visible when nothing is staged. */}
      {(state.kind === 'idle' || state.kind === 'errors' || state.kind === 'saved') && (
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          style={{
            position: 'relative', overflow: 'hidden',
            border: `2px dashed ${dragOver ? '#2563eb' : '#cbd5e1'}`,
            borderRadius: 10,
            padding: '40px 20px 36px',
            textAlign: 'center',
            background: dragOver
              ? 'linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%)'
              : 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
            transition: 'border-color 0.15s, background 0.15s',
          }}>
          <div aria-hidden style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(circle at 80% 60%, rgba(37,99,235,0.06), transparent 50%), radial-gradient(circle at 20% 80%, rgba(37,99,235,0.05), transparent 55%)',
          }} />
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            onChange={onChange}
            style={{ display: 'none' }}
          />

          <div style={{
            position: 'relative',
            width: 56, height: 56, borderRadius: 999,
            background: '#dbeafe', color: '#2563eb',
            margin: '0 auto 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.5 19a4.5 4.5 0 0 0 0-9h-1.27A7 7 0 1 0 5 17.5" />
              <polyline points="16 16 12 12 8 16" />
              <line x1="12" y1="12" x2="12" y2="21" />
            </svg>
          </div>

          <div style={{
            fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 4,
            position: 'relative',
          }}>
            {dragOver ? 'Drop file to upload' : 'Drag and drop your .xlsx file here'}
          </div>
          <div style={{
            fontSize: 13, color: '#64748b', marginBottom: 18,
            position: 'relative',
          }}>
            or click below to browse
          </div>
          <button
            onClick={onPick}
            style={{
              position: 'relative',
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 20px',
              background: '#2563eb',
              color: '#fff', border: 'none', borderRadius: 6,
              fontSize: 13.5, fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.15s',
              boxShadow: '0 1px 2px rgba(37,99,235,0.25)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#1d4ed8'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#2563eb'; }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            Choose file
          </button>
        </div>
      )}

      {/* Staged-file confirmation panel — shown after pick/drop, before
          parse. The Type radio sits inside the same card so the user
          picks Upfront vs Scatter at the same time they confirm the
          filename. Default is Upfront — matches every existing upload
          path (the xlsx parser stamps the same default in case a
          future caller bypasses this UI). */}
      {state.kind === 'pending' && (
        <StagedFilePanel
          file={state.file}
          uploadKind={uploadKind}
          onKindChange={setUploadKind}
          uploadRateCard={effectiveRateCard}
          onRateCardChange={setUploadRateCard}
          onConfirm={confirmUpload}
          onCancel={reset}
        />
      )}

      {/* Parsing-in-progress panel — replaces the drop zone while we work. */}
      {state.kind === 'parsing' && (
        <div style={{
          padding: '24px 20px', borderRadius: 10,
          border: '1px solid #bfdbfe', background: '#eff6ff',
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <Spinner />
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1e40af' }}>
              Parsing {state.fileName}
            </div>
            <div style={{ fontSize: 12, color: '#1e3a8a', marginTop: 1 }}>
              Validating columns, quarters, and HH coverage…
            </div>
          </div>
        </div>
      )}

      {/* Errors / success */}
      {state.kind === 'errors' && (
        <div style={{ marginTop: 18 }}>
          <Panel tone="error" icon="✕" title={`Could not upload ${state.fileName}`}>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.55 }}>
              {state.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </Panel>
        </div>
      )}
      {state.kind === 'saved' && (
        <div style={{ marginTop: 18 }}>
          {/* Name the stream, not just the number. Version numbering runs
              per (year, kind, rateCard), so a bare "V1" here would sit next
              to the general master's "V4" with nothing to say which track it
              joined — the same ambiguity that stops us assigning this upload
              to the "Current Latest Version" card above. */}
          <Panel
            tone="success"
            icon="✓"
            title={`Uploaded as ${formatEstimateStream(state.version.kind, cardOf(state.version))} ${getVersionLabel(state.version)}`}
          >
            <div style={{ fontSize: 12.5, color: '#065f46', marginTop: 2 }}>
              As of {formatDate(state.version.asOfDate)} &middot;{' '}
              {state.version.rowCount.toLocaleString()} rows &middot;{' '}
              quarters {state.version.quarters.map(q => q.label).join(', ')}
            </div>
          </Panel>
        </div>
      )}
    </div>
  );
};

/**
 * Shown after a file is picked/dropped. Surfaces the filename + size so
 * the user can double-check before committing. Confirm → parse + save;
 * Cancel → reset to idle.
 */
const StagedFilePanel: React.FC<{
  file: File;
  uploadKind: EstimateKind;
  onKindChange: (k: EstimateKind) => void;
  uploadRateCard: RateCard;
  onRateCardChange: (c: RateCard) => void;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ file, uploadKind, onKindChange, uploadRateCard, onRateCardChange, onConfirm, onCancel }) => (
  <div style={{
    padding: '18px 20px', borderRadius: 10,
    border: '1px solid #cbd5e1', background: '#fff',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{
        width: 42, height: 42, borderRadius: 8,
        background: '#eff6ff', color: '#2563eb',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 600, color: '#0f172a',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {file.name}
        </div>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          {formatBytes(file.size)} &middot; ready to upload
        </div>
      </div>
      <button
        onClick={onCancel}
        title="Discard and pick a different file"
        style={{
          padding: '8px 14px',
          background: '#fff', color: '#475569',
          border: '1px solid #cbd5e1', borderRadius: 6,
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
        Cancel
      </button>
      <button
        onClick={onConfirm}
        title="Parse and save this file as a new estimate version"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 18px',
          background: '#2563eb', color: '#fff',
          border: 'none', borderRadius: 6,
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(37,99,235,0.25)',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#1d4ed8'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#2563eb'; }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.4"
          strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Confirm Upload
      </button>
    </div>
    {/* Type picker — Upfront vs Scatter. Two-button segmented control,
        compact so it sits between the file row and the help text
        without crowding either. Versioning is per (year, kind) so
        flipping this changes which sequence the new upload joins. */}
    <div style={{
      marginTop: 14, paddingTop: 12, borderTop: '1px solid #f1f5f9',
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <span style={{
        fontSize: 11, fontWeight: 700, color: '#64748b',
        textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>
        Type
      </span>
      <div role="radiogroup" style={{
        display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: 6,
        overflow: 'hidden',
      }}>
        {ESTIMATE_KINDS.map(k => {
          const selected = k === uploadKind;
          return (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onKindChange(k)}
              style={{
                padding: '6px 14px',
                background: selected ? '#2563eb' : '#fff',
                color: selected ? '#fff' : '#475569',
                border: 'none',
                fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {formatEstimateKind(k)}
            </button>
          );
        })}
      </div>
      <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
        {uploadKind === 'scatter'
          ? 'Will be versioned in the Scatter track.'
          : `Will be versioned in the ${formatEstimateStream('upfront', uploadRateCard)} track.`}
      </span>
    </div>
    {/* Rate card picker — General vs a customized agency card (WPP today).
        Upfront only: a rate card is a variant OF an upfront, not a peer of
        upfront and scatter, and there is no WPP scatter. Hidden rather than
        disabled when Scatter is picked so it can't be misread as an option
        that exists but is unavailable.

        A selector rather than a checkbox because a boolean can't grow a
        third card — you'd end up with isWpp plus isOmnicom plus a tiebreak
        rule. Two options today, same cost to build, free to extend. */}
    {uploadKind === 'upfront' && (
      <div style={{
        marginTop: 10, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: '#64748b',
          textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          Rate Card
        </span>
        <div role="radiogroup" style={{
          display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: 6,
          overflow: 'hidden',
        }}>
          {RATE_CARDS.map(c => {
            const selected = c === uploadRateCard;
            return (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => onRateCardChange(c)}
                style={{
                  padding: '6px 14px',
                  background: selected ? '#7c3aed' : '#fff',
                  color: selected ? '#fff' : '#475569',
                  border: 'none',
                  fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {formatRateCard(c) ?? 'General'}
              </button>
            );
          })}
        </div>
        <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
          {uploadRateCard === 'general'
            ? 'The master estimate — used by every plan unless it says otherwise.'
            : 'Only plans created on this rate card will use it.'}
        </span>
      </div>
    )}
    <div style={{
      marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9',
      fontSize: 12, color: '#64748b',
    }}>
      Double-check the file name and type above. Click <strong>Confirm Upload</strong> to
      validate and save as a new version, or <strong>Cancel</strong> to pick a
      different file.
    </div>
  </div>
);

const Spinner: React.FC = () => (
  <div style={{
    width: 22, height: 22, borderRadius: '50%',
    border: '2.5px solid #bfdbfe', borderTopColor: '#2563eb',
    animation: 'spin 0.7s linear infinite',
    flexShrink: 0,
  }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const Panel: React.FC<{
  tone: 'info' | 'error' | 'success';
  icon: string;
  title: string;
  children?: React.ReactNode;
}> = ({ tone, icon, title, children }) => {
  const palette = {
    info:    { bg: '#eff6ff', border: '#bfdbfe', accent: '#1e40af', iconBg: '#dbeafe' },
    error:   { bg: '#fef2f2', border: '#fecaca', accent: '#991b1b', iconBg: '#fee2e2' },
    success: { bg: '#ecfdf5', border: '#a7f3d0', accent: '#065f46', iconBg: '#d1fae5' },
  }[tone];
  return (
    <div style={{
      display: 'flex', gap: 12,
      padding: '12px 16px', borderRadius: 8,
      background: palette.bg, border: `1px solid ${palette.border}`,
    }}>
      <div style={{
        flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
        background: palette.iconBg, color: palette.accent,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 700, marginTop: 1,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: palette.accent }}>{title}</div>
        {children}
      </div>
    </div>
  );
};

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default UploadTab;
